import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { ModalContainer } from "../PageComponents";
import ProfileImage from "../../../src/assets/images/ProfileImage.svg";
import { LabelInput } from "../Reusable/Layout";
import { GamerTags } from "./GamerTags";
import { PurchaseTable } from "./PurchaseTable";
import { purchases } from "../../assets/DemoData";
import { CloseIcon } from "../../icons/promotions";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { updateUser, fetchUserById } from "../../features/auth/store/userSlice";
import { UserDetailModalInput, UserFormData } from "./types";

const userValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone_number: Yup.string()
    .min(10, "Phone number must be at least 10 characters")
    .max(15, "Phone number must not exceed 15 characters")
    .required("Phone number is required"),
  status: Yup.string().required("Status is required"),
  role: Yup.string().required("Role is required"),
});

export const UserDetailModal = ({
  userId,
  showTabs = true,
  tabItems = ["Details", "FAQ"],
  onClose,
  isOpen,
}: UserDetailModalInput) => {
  const dispatch = useAppDispatch();
  const [toggle, setToggle] = useState<string>(tabItems[0] || "Details");
  const { selectedUser, loading, error } = useAppSelector(
    (state) => state.users,
  );

  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    country: "",
    firstName: "",
    lastName: "",
    email: "",
    phone_number: "",
    birthDate: "",
    status: "",
    role: "",
  });

  // Fetch user data when modal opens or userId changes
  useEffect(() => {
    if (isOpen && userId) {
      dispatch(fetchUserById(userId));
    }
  }, [dispatch, isOpen, userId]);

  // Update form data when user data is loaded
  useEffect(() => {
    if (selectedUser) {
      setFormData({
        username: selectedUser.username || "",
        country: selectedUser.country || "",
        firstName: selectedUser.firstname || "",
        lastName: selectedUser.lastname || "",
        email: selectedUser.email || "",
        phone_number: selectedUser.phone_number || "",
        birthDate: selectedUser.birth_date || selectedUser.birthDate || "",
        status: selectedUser.status || "",
        role: selectedUser.role || "",
      });
    }
  }, [selectedUser]);

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Validate form data using Yup
      await userValidationSchema.validate(formData, { abortEarly: false });

      // Prepare data for API
      const updateData = {
        email: formData.email,
        phone_number: formData.phone_number,
        status: formData.status,
        role: formData.role,
        firstname: formData.firstName,
        lastname: formData.lastName,
        username: formData.username,
        country: formData.country,
        birth_date: formData.birthDate,
      };

      await dispatch(
        updateUser({ user_id: userId, data: updateData }),
      ).unwrap();
      toast.success("User updated successfully!");
    } catch (error: any) {
      if (error.inner) {
        // Yup validation errors
        const validationErrors = error.inner
          .map((err: any) => err.message)
          .join(", ");
        toast.error(`Validation errors: ${validationErrors}`);
      } else {
        // API or other errors
        toast.error("Failed to update user");
      }
    }
  };

  if (!isOpen) return null;

  // Show loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
          <p className="text-center">Loading user details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !selectedUser) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
          <p className="text-center text-red-500 mb-4">
            Error loading user details
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => dispatch(fetchUserById(userId))}
              className="px-4 py-2 bg-blue-500 text-white rounded-md"
            >
              Retry
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Create a function to render different content based on toggle state
  const renderContent = () => {
    switch (toggle) {
      case "Personal Information":
        return (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelInput
                  type="text"
                  value={formData.username}
                  placeholder="Username"
                  name="username"
                  onChange={(e) =>
                    handleInputChange("username", e.target.value)
                  }
                />
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.country}
                  placeholder="Country"
                  name="country"
                  onChange={(e) => handleInputChange("country", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelInput
                  type="text"
                  value={formData.firstName}
                  placeholder="First Name"
                  name="firstName"
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                />
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.lastName}
                  placeholder="Last Name"
                  name="lastName"
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelInput
                  type="email"
                  value={formData.email}
                  placeholder="Email Address"
                  name="email"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
              <div>
                <LabelInput
                  type="tel"
                  value={formData.phone_number}
                  placeholder="Phone Number"
                  name="phone"
                  onChange={(e) =>
                    handleInputChange("phone_number", e.target.value)
                  }
                />
              </div>
            </div>
            <div>
              <LabelInput
                type="date"
                value={formData.birthDate}
                placeholder="Birth Date"
                name="birthDate"
                onChange={(e) => handleInputChange("birthDate", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Status</option>
                  <option value="active">Active</option>
                  <option value="deleted">Deleted</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Role</option>
                  <option value="user">User</option>
                  <option value="customer_service">
                    Admin (Default: Customer Service)
                  </option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveChanges}
                disabled={loading}
                className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        );
      case "Gamer Tags":
        return <GamerTags />;
      case "Purchases":
        return <PurchaseTable purchases={purchases} />;
    }
  };

  return (
    <div>
      <ModalContainer
        title="User Details"
        closeIcon={
          <button onClick={onClose}>
            <CloseIcon />
          </button>
        }
      >
        <div className="bg-BG-light dark:bg-BG-dark dark:text-white flex items-center p-4 rounded-md mb-4">
          <div className="mr-4">
            <img
              src={selectedUser?.profile_image || ProfileImage}
              alt="Profile"
              className="w-[160px] h-[160px] rounded-full"
            />
          </div>

          <div>
            <p className="font-bold text-[40px] ">
              {selectedUser
                ? `${selectedUser.firstname || ""} ${
                    selectedUser.lastname || ""
                  }`.trim() || "N/A"
                : "Loading..."}
            </p>
            <div className="flex items-center gap-4 ">
              <p className="text-sm">{selectedUser?.username || "N/A"}</p>
              <span className="text-lg">|</span>
              <p className="text-sm">
                Joining date:{" "}
                {selectedUser?.created_at
                  ? new Date(selectedUser.created_at).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div className="bg-amber-50 text-amber-500 rounded-lg w-32">
              <p className="p-2 text-base">
                {selectedUser?.status || "Unknown"}
              </p>
            </div>
          </div>
        </div>

        {showTabs && tabItems.length > 0 && (
          <div className="rounded-lg p-[6px] bg-[#F4F4F5] dark:bg-BG-dark w-4/4 h-[42px] flex">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`flex-1 py-[6px] px-[6px] bp:px-[10px] text-xs place-content-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        {/* Render content based on the selected tab */}
        {renderContent()}
      </ModalContainer>
    </div>
  );
};
